import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Skeleton, Stack, Tabs, Typography } from '@mui/material';
import PromptDialog from 'core/components/Dialogs/PromptDialog';
import EmptyMessage from 'core/components/EmptyMessage';
import A11yTab from 'core/components/Tabs/A11yTab';
import A11yTabPanel from 'core/components/Tabs/A11yTabPanel';
import { currencyFormatter, isNumeric } from 'core/utils/commonUtils';
import { getTotalSum } from 'core/utils/productUtils';
import useOrderProjects, {
  SWR_ORDER_PROJECTS_ENDPOINT,
} from 'hooks/swr/useOrderProjects';
import _, { cloneDeep, isArray, isBoolean } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useContext, type FC, useState, useMemo } from 'react';
import * as orderProjectService from 'services/orderProjectService';
import StoreContext from 'stores/context/StoreContext';
import { mutate } from 'swr';
import OrderProjectEdit from './OrderProjectEdit';
import type { OrderProject } from 'models/order/orderProject';

const TAB_PREFIX = 'projects';

const getActiveProjectsSum = (projects: OrderProject[]): number =>
  getTotalSum(
    _(projects)
      .filter((p) => p.active)
      .map((p) => p.orderProducts)
      .flatMap()
      .value()
  );

const OrderProjectsEdit: FC<OrderProjectsEditParams> = observer(({ orderId }) => {
  // const orderPageStore = useOrderPageStore();
  const { userStore } = useContext(StoreContext);
  const [addLoading, setAddLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [projectDialogOpen, setProjectDialogOpen] = useState<
    | false
    | {
        open: boolean;
        onSave?: ((name: string) => void) | null;
        defaultName?: string;
      }
  >(false);

  const { data: projects, isLoading } = useOrderProjects(
    orderId,
    isNumeric(orderId ?? '')
  );

  const activeProjectsSum = useMemo(
    () => getActiveProjectsSum(projects ?? []),
    [projects]
  );

  const [activeProjects, notActiveProjects] = useMemo(
    () =>
      _(projects)
        .sortBy((p) => p.user?.id !== userStore.userData?.id)
        .partition((p) => p.active)
        .value(),
    [projects, userStore.userData]
  );

  const isActiveTab = (): boolean => currentTab === 0;

  const handleAddProject = (): void => {
    setProjectDialogOpen({
      open: true,
      onSave: handleOnSaveCreateDialog,
      defaultName: 'Новый проект',
    });

    // orderPageStore.addProject({
    //   id: -Date.now(),
    //   active: isActiveTab(),
    //   name: 'Новый проект',
    //   dateCreate: new Date(),
    //   orderProducts: [],
    //   user: !isNil(userStore.userData)
    //     ? ({
    //         id: userStore.userData.id,
    //         fullName: getFullName(userStore.userData),
    //       } satisfies UserInfo)
    //     : undefined,
    // });
  };

  function handleCopyProject(project: OrderProject): void {
    setProjectDialogOpen({
      open: true,
      defaultName: `${project.name} (Копия)`,
      onSave: (name) => {
        setAddLoading(true);
        copyProject(project.id, name, project.active)
          .then(async (p) => {
            setAddLoading(false);
            setProjectDialogOpen(false);

            return p;
          })
          .catch((e) => {
            setAddLoading(false);
            console.log(e);
          });
      },
    });
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
    setCurrentTab(newValue);
  };

  function handleOnCloseCreateDialog(): void {
    setProjectDialogOpen(false);
  }

  const createProject = async (
    defaultValue: Partial<OrderProject>
  ): Promise<Array<OrderProject[] | undefined>> =>
    await mutate<OrderProject[]>(
      (key) => isArray(key) && key[0] === SWR_ORDER_PROJECTS_ENDPOINT,
      orderProjectService
        .createOrderProjectAsync(orderId, {
          name: defaultValue.name ?? 'Новый проект',
          active: defaultValue.active ?? false,
        })
        .then((p) => [p, ...cloneDeep(projects ?? [])]),
      {
        rollbackOnError: true,
        populateCache: true,
        revalidate: false,
      }
    );

  const copyProject = async (
    projectId: number,
    name: string,
    active: boolean
  ): Promise<Array<OrderProject[] | undefined>> =>
    await mutate<OrderProject[]>(
      (key) => isArray(key) && key[0] === SWR_ORDER_PROJECTS_ENDPOINT,
      orderProjectService
        .copyOrderProjectAsync(orderId, projectId, {
          name,
          active,
        })
        .then((p) => [p, ...cloneDeep(projects ?? [])]),
      {
        rollbackOnError: true,
        populateCache: true,
        revalidate: false,
      }
    );

  function handleOnSaveCreateDialog(projectName: string): void {
    setAddLoading(true);

    createProject({ name: projectName, active: isActiveTab() })
      .then(async (p) => {
        setAddLoading(false);
        setProjectDialogOpen(false);

        return p;
      })
      .catch((e) => {
        setAddLoading(false);
        console.log(e);
      });
  }

  if (isLoading) {
    return (
      <Stack gap={2}>
        <Stack direction={'row'} gap={2}>
          <Skeleton variant='rounded' width={110} height={48} />
          <Skeleton variant='rounded' width={110} height={48} />
        </Stack>

        <Skeleton variant='rounded' width={200} />
        <Skeleton variant='rounded' width={300} />

        <Stack direction={'row'} gap={2}>
          <Skeleton variant='rounded' width={160} height={35} />
          <Skeleton variant='rounded' width={230} height={35} />
        </Stack>

        <Skeleton variant='rounded' height={200} />
      </Stack>
    );
  }

  return (
    <Stack rowGap={2}>
      <Stack
        direction={'row'}
        columnGap={2}
        rowGap={1}
        flexWrap={'wrap'}
        alignItems={'center'}>
        <Button variant='outlined' onClick={handleAddProject}>
          Добавить проект
        </Button>

        <Typography variant='subtitle1'>
          Общая стоимость активных проектов: {currencyFormatter.format(activeProjectsSum)}
        </Typography>
      </Stack>

      <Tabs
        sx={{ borderBottom: 1, borderColor: 'divider' }}
        value={currentTab}
        onChange={handleTabChange}
        aria-label='project active tabs'>
        <A11yTab label='Активные' index={0} prefix={TAB_PREFIX} />
        <A11yTab label='Неактивные' index={1} prefix={TAB_PREFIX} />
      </Tabs>

      <A11yTabPanel index={0} prefix={TAB_PREFIX} value={currentTab} disablePadding>
        <Stack direction={'column'} rowGap={3}>
          {activeProjects.map((p) => (
            <OrderProjectEdit
              orderId={orderId}
              projects={projects ?? []}
              key={`project${p.id}`}
              project={p}
              onCopy={handleCopyProject}
            />
          ))}

          {!activeProjects.length && <EmptyMessage />}
        </Stack>
      </A11yTabPanel>
      <A11yTabPanel index={1} prefix={TAB_PREFIX} value={currentTab} disablePadding>
        <Stack direction={'column'} rowGap={3}>
          {notActiveProjects.map((p) => (
            <OrderProjectEdit
              orderId={orderId}
              projects={projects ?? []}
              key={`project${p.id}`}
              project={p}
              onCopy={handleCopyProject}
            />
          ))}

          {!notActiveProjects.length && <EmptyMessage />}
        </Stack>
      </A11yTabPanel>

      {!isBoolean(projectDialogOpen) && (
        <PromptDialog
          open={projectDialogOpen.open}
          title={'Создание проекта'}
          maxWidth={'xs'}
          fullWidth
          defaultValue={projectDialogOpen.defaultName ?? ''}
          textFiledProps={{
            label: 'Название проекта',
          }}
          saveButtonProps={{ component: LoadingButton, loading: addLoading }}
          onClose={handleOnCloseCreateDialog}
          onSave={
            projectDialogOpen.onSave ??
            (() => {
              void 0;
            })
          }
        />
      )}
    </Stack>
  );
});

export interface OrderProjectsEditParams {
  orderId: number;
}

export default OrderProjectsEdit;
